import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import fetchProduct from "../../helpers/useProduct/fetchProduct";
import formatDatalayerEcommerce from "../../helpers/formatDatalayerEcommerce";
import { SWATCH_TOTAL_CAP, swatchLimitEach } from "../../helpers/swatchCap";

const sumQuantities = (basket) =>
  Object.values(basket).reduce((total, quantity) => total + quantity, 0);

/** window.addToCart / window.updateCart both return undefined and report through callbacks. */
const runCartAction = (action) =>
  new Promise((resolve, reject) => {
    action({ onAfterAdd: resolve, onError: reject });
  });

/**
 * The drawer's local basket of free swatches, reconciled with the cart on a
 * single submit.
 *
 * @param {Object} param
 * @param {Array} param.items - swatchDrawerItems output
 * @param {boolean} param.open - whether the drawer is open
 * @param {string} [param.productTitle] - the PDP product, recorded on each line
 */
export default function useSwatchBasket({ items, open, productTitle }) {
  const [cartData, setCartData] = useState(window.cartData);
  const [basket, setBasket] = useState({});

  useEffect(() => {
    const onCartUpdated = (e) => setCartData(e.detail.cartData);

    document.addEventListener("cart:updated", onCartUpdated);

    return () => document.removeEventListener("cart:updated", onCartUpdated);
  }, []);

  const limitEach = swatchLimitEach();

  // Keyed by the cart's numeric variant id, which is also what /cart/add takes.
  const swatchCatalogue = useMemo(() => {
    const catalogue = new Map();

    items.forEach(({ swatchProduct, image, optionValueName }) => {
      if (!swatchProduct) return;

      catalogue.set(window.gidToId(swatchProduct.variantId), {
        productGid: swatchProduct.gid,
        swatchType: swatchProduct.swatchType,
        price: swatchProduct.price,
        image,
        optionValueName,
      });
    });

    return catalogue;
  }, [items]);

  const cartSwatchLines = useMemo(() => {
    const lines = new Map();

    // item.id, not item.key — that's the handle the rest of the theme updates by.
    (cartData?.items || []).forEach((item) => {
      if (item.properties?._is_swatch !== "true") return;

      lines.set(String(item.variant_id), {
        lineId: item.id,
        quantity: item.quantity,
      });
    });

    return lines;
  }, [cartData]);

  const cartLoaded = Array.isArray(cartData?.items);

  useEffect(() => {
    if (!open) return;

    const seeded = {};

    cartSwatchLines.forEach(({ quantity }, variantId) => {
      if (!swatchCatalogue.has(variantId)) return;

      seeded[variantId] = Math.min(quantity, limitEach);
    });

    setBasket(seeded);
    // Seeds once per open (and once when the cart first arrives — window.cartData
    // starts as {}). cartSwatchLines and swatchCatalogue are read but deliberately
    // NOT dependencies: a deselection in the drawer reads as "basket qty < cart
    // qty", indistinguishable from the cart having just gained that line, so
    // re-seeding on cart:updated would silently undo it. With no dimmer the
    // mini-cart sits visible behind this drawer, making that interleaving likely.
    // swatchCatalogue would also re-seed on every chip click, since `items`
    // carries the selection.
  }, [open, cartLoaded]);

  const basketCount = sumQuantities(basket);

  // Cart swatches this drawer has no chip for (added from another PDP) still
  // count against the cap — hence the "remove swatches from cart" wording.
  const swatchesElsewhere = useMemo(() => {
    let total = 0;

    cartSwatchLines.forEach(({ quantity }, variantId) => {
      if (swatchCatalogue.has(variantId)) return;

      total += quantity;
    });

    return total;
  }, [cartSwatchLines, swatchCatalogue]);

  // Trade groups get a per-swatch allowance instead of a cart-wide cap.
  const capped = limitEach === 1;
  const canAddMore =
    !capped || basketCount + swatchesElsewhere < SWATCH_TOTAL_CAP;

  const setQuantity = useCallback(
    (variantId, quantity) => {
      setBasket((current) => {
        const next = { ...current };
        const target = Math.max(0, Math.min(quantity, limitEach));

        if (!target) {
          delete next[variantId];

          return next;
        }

        // Re-checked against `current` so a stale render can't slip one past.
        const others = Object.entries(current)
          .filter(([id]) => id !== variantId)
          .reduce((total, [, qty]) => total + qty, 0);

        if (capped && others + swatchesElsewhere + target > SWATCH_TOTAL_CAP) {
          return current;
        }

        next[variantId] = target;

        return next;
      });
    },
    [capped, limitEach, swatchesElsewhere],
  );

  const toggle = useCallback(
    (variantId) => setQuantity(variantId, basket[variantId] ? 0 : 1),
    [basket, setQuantity],
  );

  const changes = useMemo(() => {
    const adds = [];
    const updates = {};

    swatchCatalogue.forEach(({ swatchType }, variantId) => {
      const line = cartSwatchLines.get(variantId);
      const have = line?.quantity || 0;
      const want = basket[variantId] || 0;

      if (want === have) return;

      if (want > have) {
        // /cart/add merges into a line with matching properties, so this is the
        // delta, not the target.
        adds.push({
          id: variantId,
          quantity: want - have,
          properties: {
            _is_swatch: "true",
            _swatch_type: swatchType,
            ...(productTitle ? { _swatch_added_from: productTitle } : {}),
          },
        });

        return;
      }

      updates[line.lineId] = want;
    });

    return { adds, updates };
  }, [basket, cartSwatchLines, swatchCatalogue, productTitle]);

  const hasChanges =
    changes.adds.length > 0 || Object.keys(changes.updates).length > 0;

  const pushAddEvents = useCallback(
    (adds) => {
      // formatDatalayerEcommerce hard-codes a single-element products array, so
      // this is one event per line. fetchProduct is memoized per gid.
      adds.forEach(({ id, quantity }) => {
        const entry = swatchCatalogue.get(id);

        if (!entry) return;

        fetchProduct(entry.productGid).then((product) => {
          const variant = product.variants.find(
            (candidate) =>
              candidate.id === `gid://shopify/ProductVariant/${id}`,
          );

          if (!variant) return;

          window.ElevarDataLayer?.push({
            event: "dl_add_to_cart",
            ...formatDatalayerEcommerce({
              product,
              variant,
              key: "add",
              customProperties: {
                is_quick_view: false,
                is_product_set: false,
                is_buy_now_button: false,
                quantity,
              },
            }),
          });
        });
      });
    },
    [swatchCatalogue],
  );

  const submit = useCallback(async () => {
    const { adds, updates } = changes;

    // Sequenced, never concurrent: updateCartData bumps latestUpdateId at call
    // time and bails if another call started since, so two requests in flight
    // let whichever *started* second win on network latency rather than recency.
    // Removals go first — they free headroom under the cap before adds land.
    if (Object.keys(updates).length) {
      await runCartAction((options) => window.updateCart({ updates }, options));
    }

    if (adds.length) {
      await runCartAction((options) => window.addToCart(adds, options));

      // Only the add leg: window.updateCart already pushes its own
      // dl_add_to_cart / dl_remove_from_cart per entry.
      pushAddEvents(adds);
    }
  }, [changes, pushAddEvents]);

  return {
    basket,
    basketCount,
    canAddMore,
    capped,
    hasChanges,
    limitEach,
    setQuantity,
    submit,
    swatchCatalogue,
    toggle,
    totalCap: SWATCH_TOTAL_CAP,
  };
}
